import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

/**
 * Plain query-string search handling.
 *
 * TanStack's default serializer JSON-encodes values that look numeric or
 * boolean (`?classic=1` -> `?classic=%221%22`), which breaks every call site
 * in the router-compat shim that compares raw strings. These use standard
 * URLSearchParams semantics so params round-trip exactly as written.
 */
const parseSearch = (searchStr: string): Record<string, string> => {
  const out: Record<string, string> = {};
  new URLSearchParams(searchStr).forEach((value, key) => {
    // Tolerate links already in the wild that carry JSON-quoted values.
    out[key] = value.replace(/^"|"$/g, "");
  });
  return out;
};

const stringifySearch = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search ?? {})) {
    if (value === undefined || value === null) continue;
    params.set(key, typeof value === "string" ? value : JSON.stringify(value));
  }
  const str = params.toString();
  return str ? `?${str}` : "";
};

export const getRouter = () => {
  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    parseSearch,
    stringifySearch,
  });

  return router;
};